import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';

import { Store } from '@ngrx/store';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { AlertService } from 'src/app/core/services/alert.service';

import { toggleAlertMode } from 'src/app/core/actions/feed.actions';

import { Alert } from 'src/app/core/models/alert.models';

import { AppState } from 'src/app/reducers';

interface AlertInfo {
  icon: IconProp;
  label: string;
  value: any;
  highlight?: boolean;
  url?: string;
}

@Component({
  selector: 'app-alert-card',
  templateUrl: './alert-card.component.html',
  styleUrls: ['./alert-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertCardComponent implements OnInit {
  @Input() alert: Alert;
  @Input() searchQuery: string;

  @Output() alertEdit = new EventEmitter<Alert>();
  @Output() alertDelete = new EventEmitter<Alert>();

  generalAlertInfo: AlertInfo[];
  postTriggerAlertInfo: AlertInfo[];
  volumeTriggerAlertInfo: AlertInfo[];

  constructor(
    private datePipe: DatePipe,
    private store: Store<AppState>,
    public alertService: AlertService,
  ) {}

  ngOnInit(): void {
    const deviationPeriod = (this.alert?.deviation_period ?? 0) / 3600;
    const deviationPeriodLabel =
      deviationPeriod === 1 ? 'Last Hour' : `Last ${deviationPeriod} Hours`;
    const lastTriggeredTime = this.alert.last_triggered_timestamp;
    const timeCreated = this.alert?.time_created?.timestamp;
    this.generalAlertInfo = [
      {
        icon: ['fas', 'earth-americas'],
        label: 'alert-card.title.on-object',
        value: this.alert?.feed_title,
        highlight: true,
        url: `analytics/workspace/${this.alert.workspace_id}/feed/${this.alert['feed_id']}`,
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.last-triggered-time',
        value:
          this.alert?.last_triggered_timestamp !== 0
            ? this.datePipe.transform(
                lastTriggeredTime * 1000,
                'dd.MM.yyyy, HH:mm',
              )
            : 'n/a',
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.created-on',
        value: this.datePipe.transform(timeCreated * 1000, 'dd.MM.yyyy, HH:mm'),
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.created-by',
        value: this.alert?.created_by_user?.name,
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.trigger-condition',
        value: this.alert?.trigger_type?.title,
      },
    ];

    this.postTriggerAlertInfo = [
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.how-many-posts-to-send',
        value: this.alert?.notification_posts_count,
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.trigger-on-every-n-new-posts',
        value: this.alert?.deviation_posts_count,
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.recipients-count',
        value: this.alert?.notify_principals_count,
      },
    ];

    this.volumeTriggerAlertInfo = [
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.change-direction',
        value: this.alert?.deviation_direction?.title,
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.change-amount',
        value: `On ${this.alert?.deviation_threshold_value}% Change`,
      },
      {
        icon: ['fas', 'check'],
        label: 'alert-card.title.compared-to',
        value: deviationPeriodLabel,
      },
    ];
  }

  toggleAlertMode(alertId: string) {
    this.store.dispatch(toggleAlertMode({ alertId }));
  }

  editAlert(): void {
    this.alertEdit.emit(this.alert);
  }

  deleteAlert(): void {
    this.alertDelete.emit(this.alert);
  }
}
